/**
 * mSTEM Share Project Modal container (PRJ-04 / LLD UC 11).
 *
 * Owns local 2-step state machine (step 1 = form, step 2 = link reveal) +
 * audience / permission / message / errors / copyState.
 *
 * Step 1 (form submit) flow:
 *   1. Validate audience non-empty and permission non-empty.
 *   2. On error: render inline error span keyed by error code.
 *   3. On success: buildShareLink() to generate mock share URL, switch to
 *      step 2, show projectShared toast.
 *
 * Step 2 (copy link) flow:
 *   1. copyToClipboard(link) — prefers navigator.clipboard, falls back to
 *      execCommand for non-HTTPS browsers.
 *   2. On resolve: copyState='copied' for COPY_REVERT_MS (2s) then reverts
 *      to 'idle'; linkCopied toast shown.
 *   3. On reject: copyState='failed'; linkCopyFailed toast shown so user
 *      can copy manually from the read-only input.
 *
 * No menu-bar wiring here — Wave 5 (Plan 03-09) handles File menu and
 * Ctrl+Shift+S shortcut to avoid cross-wave merge collisions.
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {injectIntl} from 'react-intl';

import {closeShareProjectModal} from '../reducers/modals';
import {showAlertWithTimeout} from '../reducers/alerts';
import {buildShareLink, copyToClipboard} from '../lib/mstem-share-link';
import SHARE_AUDIENCES from '../lib/mstem-mock/share-audiences';
import log from '../lib/log';

import ShareProjectModalComponent from '../components/share-project-modal/share-project-modal.jsx';

const COPY_REVERT_MS = 2000;

class ShareProjectModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleChangeAudience',
            'handleChangePermission',
            'handleChangeMessage',
            'handleSubmit',
            'handleCancel',
            'handleCopy',
            'handleClose',
            'revertCopy'
        ]);
        this.state = {
            step: 1,
            audience: '',
            permission: 'view',
            message: '',
            link: '',
            copyState: 'idle',
            errors: {}
        };
        this.revertTimer = null;
    }
    componentWillUnmount () {
        if (this.revertTimer) {
            clearTimeout(this.revertTimer);
            this.revertTimer = null;
        }
    }
    handleChangeAudience (e) {
        this.setState({
            audience: e.target.value,
            errors: Object.assign({}, this.state.errors, {audience: null})
        });
    }
    handleChangePermission (e) {
        this.setState({
            permission: e.target.value,
            errors: Object.assign({}, this.state.errors, {permission: null})
        });
    }
    handleChangeMessage (e) {
        this.setState({message: e.target.value});
    }
    handleSubmit (e) {
        if (e && e.preventDefault) e.preventDefault();
        const errors = {};
        if (!this.state.audience) errors.audience = 'errAudienceRequired';
        if (!this.state.permission) errors.permission = 'errPermissionRequired';
        if (errors.audience || errors.permission) {
            this.setState({errors: errors});
            return;
        }
        const link = buildShareLink();
        this.setState({
            step: 2,
            link: link,
            errors: {},
            copyState: 'idle'
        });
        this.props.onShowShared();
    }
    handleCopy () {
        if (this.state.copyState === 'copying') return;
        this.setState({copyState: 'copying'});
        copyToClipboard(this.state.link)
            .then(() => {
                this.setState({copyState: 'copied'});
                this.props.onShowLinkCopied();
                if (this.revertTimer) clearTimeout(this.revertTimer);
                this.revertTimer = setTimeout(this.revertCopy, COPY_REVERT_MS);
            })
            .catch(err => {
                log.warn('ShareProjectModal copy error', err);
                this.setState({copyState: 'failed'});
                this.props.onShowLinkCopyFailed();
            });
    }
    revertCopy () {
        this.setState({copyState: 'idle'});
        this.revertTimer = null;
    }
    handleCancel () {
        this.props.onClose();
    }
    handleClose () {
        this.props.onClose();
    }
    render () {
        return (
            <ShareProjectModalComponent
                audience={this.state.audience}
                audiences={SHARE_AUDIENCES}
                copyState={this.state.copyState}
                errors={this.state.errors}
                link={this.state.link}
                message={this.state.message}
                permission={this.state.permission}
                projectName={this.props.projectName}
                step={this.state.step}
                onCancel={this.handleCancel}
                onChangeAudience={this.handleChangeAudience}
                onChangeMessage={this.handleChangeMessage}
                onChangePermission={this.handleChangePermission}
                onClose={this.handleClose}
                onCopy={this.handleCopy}
                onSubmit={this.handleSubmit}
            />
        );
    }
}

ShareProjectModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onShowLinkCopied: PropTypes.func.isRequired,
    onShowLinkCopyFailed: PropTypes.func.isRequired,
    onShowShared: PropTypes.func.isRequired,
    projectName: PropTypes.string.isRequired
};

const mapStateToProps = state => ({
    projectName: state.scratchGui.projectTitle || ''
});

const mapDispatchToProps = dispatch => ({
    onClose: () => dispatch(closeShareProjectModal()),
    onShowShared: () => showAlertWithTimeout(dispatch, 'projectShared'),
    onShowLinkCopied: () => showAlertWithTimeout(dispatch, 'linkCopied'),
    onShowLinkCopyFailed: () => showAlertWithTimeout(dispatch, 'linkCopyFailed')
});

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps)
)(ShareProjectModal);
